import { useInt } from 'hooks/useInt';

import { AccessRoutes } from 'ui/organisms/AccessRoutes/AccessRoutes';

import { ManufacturingBand } from 'landing-pages/manufacturing/sections/ManufacturingBand/ManufacturingBand';
import { ManufacturingHero } from 'landing-pages/manufacturing/sections/ManufacturingHero/ManufacturingHero';
import { ManufacturingJobs } from 'landing-pages/manufacturing/sections/ManufacturingJobs/ManufacturingJobs';
import { ManufacturingClose } from 'landing-pages/manufacturing/sections/ManufacturingClose/ManufacturingClose';
import { ManufacturingFaq } from 'landing-pages/manufacturing/sections/ManufacturingFaq/ManufacturingFaq';
import { ManufacturingFields } from 'landing-pages/manufacturing/sections/ManufacturingFields/ManufacturingFields';
import { ManufacturingMonitoring } from 'landing-pages/manufacturing/sections/ManufacturingMonitoring/ManufacturingMonitoring';
import { ManufacturingTeams } from 'landing-pages/manufacturing/sections/ManufacturingTeams/ManufacturingTeams';

import styles from './ManufacturingPage.module.scss';

type Translate = (key: string) => string;

const getAccessRoutes = (t: Translate) => [
  {
    name: t('Platform'),
    desc: t('Search, screen and export company records from the web application.'),
    actions: [{ label: t('Open the platform'), href: '/platform' }],
  },
  {
    name: 'API',
    desc: t('Query any company by registry ID. Documented endpoints, JSON responses, provenance on every value.'),
    actions: [{ label: t('Read the API docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Bulk data feeds'),
    desc: t('Full datasets and scheduled deltas, delivered to your warehouse on your cadence.'),
    actions: [{ label: t('Request a feed'), href: '/book-a-demo' }],
  },
  {
    name: 'CRM',
    desc: (
      <>
        <b>HubSpot</b>, <b>Salesforce</b> {t('and')} <b>Microsoft Dynamics</b>.{' '}
        {t('Registry fields on the record your team already works in, matched on registration number.')}
      </>
    ),
    actions: [
      { label: t('Install for HubSpot'), href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
      { label: t('Install for Salesforce'), href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
    ],
  },
  {
    name: 'MCP',
    desc: t('Connect the dataset to any MCP-compatible agent or tool, on the same key.'),
    actions: [{ label: t('MCP server'), href: '/mcp-integration' }],
  },
  {
    name: t('Assistant connectors'),
    desc: t('Pull registry data straight into your AI assistant, with the source attached to the answer.'),
    actions: [
      { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    name: 'Regis AI',
    desc: t(
      'Ask questions in natural language across the dataset and get sourced answers, in the platform or over the API.',
    ),
    actions: [
      { label: 'Regis AI', href: '/regis' },
      { label: 'Regis API', href: '/regis-api' },
    ],
  },
];

export const ManufacturingPage = () => {
  const { t } = useInt();

  return (
    <div className={styles.wrapper}>
      <ManufacturingHero />
      <ManufacturingBand />
      <ManufacturingJobs />
      <ManufacturingMonitoring />
      <ManufacturingTeams />
      <ManufacturingFields />
      <AccessRoutes
        background="midnight"
        numbered
        heading={t('How to access the data.')}
        lede={t('Seven routes into the same primary, government-sourced dataset. Pick the one that fits your stack.')}
        routes={getAccessRoutes(t)}
      />
      <ManufacturingClose />
      <ManufacturingFaq />
    </div>
  );
};
